import { ChangeEvent, FC, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSnackbar } from 'notistack';

import {
  Divider,
  Grid,
  IconButton,
  Input,
  Paper,
  Stack,
  Typography,
  Link as MuiLink,
} from '@mui/material';
import {
  Cancel as CancelIcon,
  Done as SaveIcon,
  Edit as EditIcon,
  History as HistoryIcon,
  ArrowBack as ArrowBackIcon,
} from '@mui/icons-material';

import { HistoryModal } from './components';
import { Table } from '../../components/table/table';
import { HeadCell, Row, RowCell } from '../../components/table/types';

import {
  UpdateRateMarkupDTO,
  useGetRateMarkupMutation,
  useGetRateMarkupsQuery,
  useUpdateRateMarkupMutation,
} from '../../api';
import { UTCToDateTime } from '../../utils/dates';
import type { RateMarkup } from '../../types';
import { Link, useParams } from 'react-router-dom';
import { RouterPaths } from '../../router/paths';
import { permissionsCheck } from '../../utils/permission';

export const ExchangeFees: FC = () => {
  const { t } = useTranslation();
  const { enqueueSnackbar } = useSnackbar();
  const { exchange_fee_group_id } = useParams<'exchange_fee_group_id'>();
  const hasPermissionToEditExchangeFee = permissionsCheck(['WRITE_RATE_MARKUPS']);

  // api data
  const { data: rateMarkups, isLoading: isRateMarkupsLoading } = useGetRateMarkupsQuery({
    rate_markup_group_id: Number(exchange_fee_group_id),
  });
  const [getRateMarkup, { data: rateMarkup, isLoading: isRateMarkupLoading }] =
    useGetRateMarkupMutation();
  const [updateRateMarkup, { isLoading: isUpdateRateMarkupLoading }] =
    useUpdateRateMarkupMutation();

  // edit fields logic
  const [editFields, setEditFields] = useState<Partial<RateMarkup> | null>(null);
  const handleEditFields = (e: ChangeEvent<HTMLInputElement>) => {
    const name = e.target.name;
    const value = e.target.value;

    setEditFields((prevState) => {
      if (!prevState) {
        return {
          [name]: value,
        };
      }

      return {
        ...prevState,
        [name]: value,
      };
    });
  };

  // edit mode logic
  const [activeRowId, setActiveRowId] = useState('');
  const [isEditMode, setEditMode] = useState(false);
  const startEditMode = (rateMarkup: RateMarkup) => {
    setEditFields(rateMarkup);
    setActiveRowId(rateMarkup.id);
    setEditMode(true);
  };
  const offEditMode = () => {
    setEditFields(null);
    setActiveRowId('');
    setEditMode(false);
  };

  // history modal logic
  const [historyModalShown, setHistoryModalShown] = useState(false);
  const openHistoryModal = (id: string) => {
    // e.stopPropagation();
    setHistoryModalShown(true);
    setActiveRowId(id);
  };
  const closeHistoryModal = () => {
    setHistoryModalShown(false);
    setActiveRowId('');
  };

  // get rate markup by id for history modal
  useEffect(() => {
    if (!historyModalShown || !activeRowId || !exchange_fee_group_id) return;

    getRateMarkup({
      rateMarkupId: activeRowId,
      rate_markup_group_id: Number(exchange_fee_group_id),
    });
  }, [activeRowId, historyModalShown]);

  // edit rate markup logic
  const editRateMarkup = async () => {
    if (!editFields || !editFields.from_coin_id || !editFields.to_coin_id || !exchange_fee_group_id)
      return;

    const body: UpdateRateMarkupDTO['body'] = {
      from_coin_id: editFields.from_coin_id,
      to_coin_id: editFields.to_coin_id,
      percent: Number(editFields.percent),
    };

    try {
      await updateRateMarkup({
        body,
        rate_markup_group_id: Number(exchange_fee_group_id),
      }).unwrap();
      enqueueSnackbar(t('ui.snackbar.success'), { variant: 'success' });
      offEditMode();
    } catch (e) {
      // @ts-ignore
      enqueueSnackbar(e?.data?.message ?? t('ui.snackbar.error'), { variant: 'error' });
    }
  };

  // table cells and rows
  const headCells: HeadCell[] = [
    { id: '1', label: t('exchange-fees.head-cells.from-coin') },
    { id: '2', label: t('exchange-fees.head-cells.to-coin') },
    { id: '3', label: t('exchange-fees.head-cells.markup'), align: 'right' },
    { id: '5', label: t('exchange-fees.head-cells.last-update') },
    {
      id: '6',
      label: t('exchange-fees.head-cells.actions'),
      hidden: !hasPermissionToEditExchangeFee,
    },
  ];

  const rows: Row[] | undefined = rateMarkups?.rateMarkups?.map((it) => {
    const { id, from_coin, to_coin, percent, row_start } = it;

    const cells: RowCell[] = [
      {
        name: 'from_coin',
        value: from_coin.name,
      },
      {
        name: 'to_coin',
        value: to_coin.name,
      },
      {
        name: 'percent',
        value: percent,
        align: 'right',
        formatting: (value: string) => {
          return `${value} %`;
        },
        component:
          isEditMode && activeRowId === id ? (
            <Input
              name="percent"
              type="number"
              value={editFields?.percent ?? ''}
              onChange={handleEditFields}
              inputProps={{ min: 0, step: '0.01' }}
              endAdornment="%"
              sx={{ maxWidth: '100px' }}
              disabled={isUpdateRateMarkupLoading}
            />
          ) : undefined,
      },
      {
        name: 'row_start',
        value: row_start,
        formatting: (value: string) => {
          return UTCToDateTime(value);
        },
      },
      {
        name: 'actions',
        hidden: !hasPermissionToEditExchangeFee,
        component: isEditMode ? (
          activeRowId === id ? (
            <Stack direction="row" spacing={2}>
              <IconButton
                aria-label="save"
                color="success"
                onClick={() => editRateMarkup()}
                disabled={isUpdateRateMarkupLoading}
              >
                <SaveIcon />
              </IconButton>

              <IconButton
                aria-label="cancel"
                color="error"
                onClick={() => offEditMode()}
                disabled={isUpdateRateMarkupLoading}
              >
                <CancelIcon />
              </IconButton>
            </Stack>
          ) : undefined
        ) : (
          <Stack direction="row" spacing={2}>
            <IconButton
              aria-label="edit"
              color="warning"
              onClick={() => startEditMode(it)}
              disabled={isRateMarkupsLoading}
            >
              <EditIcon />
            </IconButton>

            {/*<Tooltip title={t('exchange-fees.action-tooltips.history')}>*/}
            <IconButton
              aria-label="show-history"
              color="info"
              onClick={() => openHistoryModal(id)}
              disabled={isRateMarkupsLoading}
            >
              <HistoryIcon />
            </IconButton>
            {/*</Tooltip>*/}
          </Stack>
        ),
      },
    ];

    return { cells };
  });

  return (
    <>
      <Stack direction="row" alignItems="flex-end" spacing={2} pl={3} pt={1}>
        <MuiLink underline="hover" component={Link} to={RouterPaths.FeeGroups}>
          <Stack direction="row" alignItems="stretch" spacing={0.2}>
            <ArrowBackIcon fontSize="inherit" />
            <Typography variant="caption" component="span">
              {t('exchange-fee-groups.back-to-groups')}
            </Typography>
          </Stack>
        </MuiLink>

        <Divider orientation="vertical" />
      </Stack>
      <Grid size={{ xs: 12 }}>
        <Paper sx={{ p: 2, display: 'flex', flexDirection: 'column' }}>
          <Table headCells={headCells} rows={rows} />
        </Paper>
      </Grid>

      <HistoryModal
        open={historyModalShown}
        onClose={closeHistoryModal}
        rateMarkup={rateMarkup}
        isRateMarkupLoading={isRateMarkupLoading}
      />
    </>
  );
};
